import ManualReplyPauseSetting from './ManualReplyPauseSetting';
import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Modal,
  Box,
  MenuItem,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCircleInfo, faWarning, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { formInputSx } from '../styles/inputStyles';
import './SelectBotModal.css';
import './SocialConnectModal.css';
import './TelegramConnectModal.css';

const TELEGRAM_BLUE = '#229ED9';

// Numbered badge built as inline SVG. CSS centering of digits in circles
// is unreliable because digits sit on the alphabetic baseline and inherit
// empty descender space inside their line-box, which pushes the glyph up
// inside any flex-centered container no matter how you tweak line-height
// or padding. SVG sidesteps that entirely: we position the text's baseline
// at the optical center of the circle (y = cy + 0.35em from baseline-up to
// cap-center), so the cap-height band of the digit lines up with the
// circle's geometric center. Works identically for 1, 2, 3, 9, anything.
const StepBadge = ({ n }) => (
  <svg
    className="telegram-step-num"
    viewBox="0 0 26 26"
    width="26"
    height="26"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="13" cy="13" r="13" fill={TELEGRAM_BLUE} />
    <text
      x="13"
      y="13"
      dy="0.35em"
      textAnchor="middle"
      fontSize="15"
      fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
      fontWeight="600"
      fill="#ffffff"
    >
      {n}
    </text>
  </svg>
);

const TelegramConnectModal = ({ open, onClose, onConnected, defaultBotId }) => {
  const { t } = useTranslation();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBotId, setSelectedBotId] = useState('');
  const [telegramToken, setTelegramToken] = useState('');
  const [autoResponseMode, setAutoResponseMode] = useState('respond_all');
  const [pauseOnManualReply, setPauseOnManualReply] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      setSelectedBotId(defaultBotId || '');
      setTelegramToken('');
      setAutoResponseMode('respond_all');
      setPauseOnManualReply(true);
      setErrorMessage('');
      fetchBots();
    }
  }, [open, defaultBotId]);

  const fetchBots = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id") || ''}&include_knowledge=False`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const completedBots = data.filter((bot) => bot.status === 'completed');
      setBots(completedBots);
    } catch (err) {
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedBotId || !telegramToken.trim() || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}auth/telegram/connect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bot_id: selectedBotId,
            telegram_bot_token: telegramToken.trim(),
            auto_response_mode: autoResponseMode,
            pause_on_manual_reply: pauseOnManualReply,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data?.detail || t('connect_telegram_error');
        throw new Error(detail);
      }
      if (typeof onConnected === 'function') onConnected(data);
      onClose();
    } catch (err) {
      console.error('Error connecting Telegram:', err);
      setErrorMessage(err.message || t('connect_telegram_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="social-connect-modal"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          width: isMobile ? '90%' : '90%',
          maxWidth: 540,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <Box className="social-connect-modal-header"
          sx={{
            flexShrink: 0,
            px: isMobile ? 2.5 : 2.75,
            pt: isMobile ? 2.5 : 2.75,
            pb: 2,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div className="select-bot-modal-header" style={{ marginBottom: 0 }}>
            <FontAwesomeIcon
              icon={faTelegram}
              style={{ color: TELEGRAM_BLUE, fontSize: '2rem', marginRight: '12px' }}
            />
            <h3 className="select-bot-modal-title">{t('connect_telegram')}</h3>
            <button
              type="button"
              className="select-bot-modal-close"
              onClick={onClose}
              aria-label={t('close')}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </Box>

        <Box className="social-connect-modal-body"
          sx={{
            px: isMobile ? 2.5 : 2.75,
            pt: 2,
            pb: isMobile ? 2.5 : 2.75,
            overflowY: 'auto',
          }}
        >
        <div className="telegram-steps">
          <div className="telegram-step">
            <StepBadge n={1} />
            <div className="telegram-step-body">
              <Trans
                i18nKey="connect_telegram_step1_body_html"
                components={{
                  botfather: <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer">@BotFather</a>,
                  newbot: <code />,
                  botsuffix: <code />,
                  example: <code />,
                }}
              />
            </div>
          </div>
          <div className="telegram-step">
            <StepBadge n={2} />
            <div className="telegram-step-body">
              <Trans
                i18nKey="connect_telegram_step2_body_html"
                components={{ token: <code /> }}
              />
            </div>
          </div>
          <div className="telegram-step">
            <StepBadge n={3} />
            <div className="telegram-step-body">
              {t('connect_telegram_step3_body')}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="select-bot-loading">
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#5616ea' }} />
          </div>
        ) : bots.length === 0 ? (
          <div className="select-bot-empty">
            <FontAwesomeIcon icon={faWarning} style={{ fontSize: '2.5rem', color: '#fac22a', marginBottom: '12px' }} />
            <p>{t('connect_telegram_no_bots')}</p>
          </div>
        ) : (
          <>
            <TextField
              fullWidth
              variant="outlined"
              label={t('connect_telegram_token_label')}
              placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxYZ"
              value={telegramToken}
              onChange={(e) => setTelegramToken(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="user-settings-input telegram-token-field"
              InputProps={{
                style: { fontFamily: 'monospace' },
              }}
              sx={formInputSx}
            />

            <TextField
              select
              fullWidth
              variant="outlined"
              label={t('select_a_bot')}
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              className="user-settings-input"
              sx={{ ...formInputSx, marginTop: '14px' }}
            >
              {bots.map((bot) => (
                <MenuItem key={bot.id} value={bot.id}>
                  {bot.name || bot.domain_name || bot.id}
                </MenuItem>
              ))}
            </TextField>

            <div className="auto-response-mode-section">
              <div className="auto-response-mode-label">{t('connect_reply_behavior_label')}</div>
              <RadioGroup
                value={autoResponseMode}
                onChange={(e) => setAutoResponseMode(e.target.value)}
                className="auto-response-mode-group"
              >
                <FormControlLabel
                  value="respond_all"
                  className={`auto-response-mode-option${autoResponseMode === 'respond_all' ? ' is-selected' : ''}`}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        color: '#c4c4c4',
                        '&.Mui-checked': { color: '#5616ea' },
                      }}
                    />
                  }
                  label={
                    <div className="auto-response-mode-option-text">
                      <span className="auto-response-mode-option-title">{t('connect_reply_mode_all_title')}</span>
                      <span className="auto-response-mode-option-help">
                        {t('connect_telegram_reply_mode_all_help')}
                      </span>
                    </div>
                  }
                />
                <FormControlLabel
                  value="opt_in"
                  className={`auto-response-mode-option${autoResponseMode === 'opt_in' ? ' is-selected' : ''}`}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        color: '#c4c4c4',
                        '&.Mui-checked': { color: '#5616ea' },
                      }}
                    />
                  }
                  label={
                    <div className="auto-response-mode-option-text">
                      <span className="auto-response-mode-option-title">{t('connect_reply_mode_optin_title')}</span>
                      <span className="auto-response-mode-option-help">
                        {t('connect_reply_mode_optin_help')}
                      </span>
                    </div>
                  }
                />
              </RadioGroup>
              <ManualReplyPauseSetting platform="telegram" checked={pauseOnManualReply}
                onChange={setPauseOnManualReply} disabled={submitting} />
            </div>

            {errorMessage && (
              <div className="telegram-error">
                <FontAwesomeIcon icon={faWarning} style={{ marginRight: '8px' }} />
                {errorMessage}
              </div>
            )}

          </>
        )}
        </Box>

            <div className="select-bot-modal-actions social-connect-modal-footer">
              <Button
                onClick={onClose}
                variant="outlined"
                disabled={submitting}
                sx={{
                  color: '#5616ea',
                  borderColor: '#5616ea',
                  '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || bots.length === 0 || !selectedBotId || !telegramToken.trim() || submitting}
                variant="contained"
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                }}
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                    {t('connecting_ellipsis')}
                  </>
                ) : (
                  t('connect_telegram')
                )}
              </Button>
            </div>
      </Box>
    </Modal>
  );
};

export default TelegramConnectModal;
